import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { filter, Subscription } from 'rxjs';

import {
  ProductSortimentEntryPricingUpdateRequest,
  ProductSortimentsPricingRequest,
  ProductSortimentsPricingResponse,
} from '@app/api/marketplace/models';
import {
  SelectedProductsActions,
  SelectedProductsSelectors,
} from '@app/pricing/store/selected-products.slice';
import { DialogService } from '@shared/services/dialog.service';

@Component({
  selector: 'app-pricing-selected-products',
  templateUrl: './pricing-selected-products.component.html',
  styleUrls: ['./pricing-selected-products.component.scss'],
})
export class PricingSelectedProductsComponent implements OnInit, OnDestroy {
  @ViewChild('pricingTable') pricingTableEl?: ElementRef;
  selectedProducts: ProductSortimentsPricingResponse[] = [];
  private savedDataJson: string = '';

  backUrl?: string;

  get modified(): boolean {
    return this.savedDataJson !== JSON.stringify(this.selectedProducts);
  }

  subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private dialogService: DialogService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.queryParams.subscribe((params) => {
        const ids = params['s'];
        if (ids?.length) {
          this.fetchProducts({
            productSortimentIds: typeof ids === 'string' ? [ids] : ids,
          } as ProductSortimentsPricingRequest);
        }

        if (params['backUrl']) {
          this.backUrl = params['backUrl'];
        }
      })
    );
    this.subs.add(
      this.store
        .select(SelectedProductsSelectors.selectSelectedProducts)
        .subscribe((products) => {
          this.selectedProducts = structuredClone(products);
          this.savedDataJson = JSON.stringify(products);
        })
    );
    this.subs.add(
      this.store
        .select(SelectedProductsSelectors.selectPricesSavingLoading)
        .pipe(filter((loading) => !loading))
        .subscribe(() => {
          this.savedDataJson = JSON.stringify(this.selectedProducts);
        })
    );
  }

  private fetchProducts(
    selectedProductIds: ProductSortimentsPricingRequest
  ): void {
    this.store.dispatch(
      SelectedProductsActions.loadSelectedProducts.trigger({
        selectedProductIds,
      })
    );
  }

  onSave() {
    const invalidControl: HTMLElement =
      this.pricingTableEl?.nativeElement.querySelector('.ng-invalid');
    if (!invalidControl) {
      const convertedData = this.selectedProducts.flatMap((product) => {
        if (product.productSortimentEntries) {
          return product.productSortimentEntries.map(
            (productSortimentEntry) =>
              ({
                productSortimentEntryId:
                  productSortimentEntry.productSortimentEntryId,
                manufacturerOrImporterPrice:
                  productSortimentEntry.manufacturerOrImporterPrice,
                wholesalerPrice: productSortimentEntry.wholesalerPrice,
                retailerPrice: productSortimentEntry.retailerPrice,
                endConsumerPrice: productSortimentEntry.endConsumerPrice,
              } as ProductSortimentEntryPricingUpdateRequest)
          );
        } else return {};
      });
      this.store.dispatch(
        SelectedProductsActions.updatePrices.trigger({ data: convertedData })
      );
    }
  }

  backButtonClicked() {
    if (this.backUrl) {
      this.router.navigate([this.backUrl]);
    } else {
      history.back();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  canDeactivate() {
    return this.modified ? this.dialogService.confirmUnsavedChanges() : true;
  }
}
